import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Trophy, Medal } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { useLang } from "@/lib/i18n";
import { api } from "@/lib/api";
import { titleForLevel } from "@/lib/levels";

const BADGE_STYLES = {
  Diamond: "text-cyan-300 border-cyan-300/40 bg-cyan-300/5",
  Gold: "text-signal border-signal/40 bg-signal/5",
  Silver: "text-white/70 border-white/30 bg-white/5",
  Bronze: "text-orange-400 border-orange-400/40 bg-orange-400/5",
};

function fmt2(n) {
  return Number(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

export default function Ranking() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await api.get("/leaderboard/global");
        setData(res.data);
      } catch (e) { console.warn("leaderboard load failed:", e); }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-base text-white">
      <Navbar />
      <div className="max-w-4xl mx-auto px-4 md:px-8 py-14">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-2 text-signal">
              <Trophy className="w-5 h-5" />
              <span className="font-mono text-xs uppercase tracking-[0.3em]">{data?.season || t("season")}</span>
            </div>
            <h1 className="mt-3 font-heading font-black text-4xl tracking-tight">{t("ranking")}</h1>
          </div>
          {data && (
            <div className="font-mono text-sm text-white/40 tabular-nums">
              {data.total_players} {t("players_word")}
            </div>
          )}
        </div>

        {!data ? (
          <div className="text-white/40 font-mono animate-pulse">…</div>
        ) : (
          <>
            {data.me && data.me.rank > 10 && (
              <div className="mb-6 border border-signal/40 bg-signal/5 px-5 py-4 flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <span className="font-mono font-bold text-lg text-signal tabular-nums">#{data.me.rank}</span>
                  <span className="font-mono text-signal font-bold">{data.me.name}</span>
                  <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 ${BADGE_STYLES[data.me.badge]}`}>{data.me.badge}</span>
                </div>
                <span className={`font-mono font-bold tabular-nums ${data.me.total_profit >= 0 ? "text-profit" : "text-loss"}`}>
                  {data.me.total_profit >= 0 ? "+" : ""}{fmt2(data.me.total_profit)}
                </span>
              </div>
            )}

            <div className="border border-white/10">
              <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-white/10 font-mono text-[10px] uppercase tracking-widest text-white/40">
                <div className="col-span-1">#</div>
                <div className="col-span-5">Trader</div>
                <div className="col-span-2 text-center">{t("level")}</div>
                <div className="col-span-2 text-right">W/M</div>
                <div className="col-span-2 text-right">P&L</div>
              </div>
              {data.top.map((r, i) => (
                <motion.div
                  key={r.user_id}
                  initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.02, 0.4) }}
                  data-testid={`ranking-row-${r.rank}`}
                  className={`grid grid-cols-12 gap-2 items-center px-5 py-3 border-b border-white/5 last:border-b-0 ${r.is_you ? "bg-signal/5" : "hover:bg-panelhover/40"} transition-colors`}
                >
                  <div className="col-span-1 font-mono font-bold tabular-nums flex items-center gap-1">
                    {r.rank <= 3 ? <Medal className={`w-4 h-4 ${r.rank === 1 ? "text-signal" : r.rank === 2 ? "text-white/70" : "text-orange-400"}`} /> : null}
                    <span className={r.rank === 1 ? "text-signal" : "text-white/50"}>{r.rank}</span>
                  </div>
                  <div className="col-span-5 flex items-center gap-2 min-w-0">
                    <div className="min-w-0">
                      <div className={`font-mono truncate ${r.is_you ? "text-signal font-bold" : "text-white/85"}`}>{r.name}</div>
                      <div className="font-mono text-[10px] text-white/40 truncate">{titleForLevel(r.level)}</div>
                    </div>
                    <span className={`font-mono text-[10px] uppercase tracking-widest border px-2 py-0.5 shrink-0 ${BADGE_STYLES[r.badge]}`}>{r.badge}</span>
                  </div>
                  <div className="col-span-2 text-center font-mono text-sm tabular-nums text-white/70">Lv {r.level}</div>
                  <div className="col-span-2 text-right font-mono text-sm tabular-nums text-white/60">{r.wins}/{r.matches}</div>
                  <div className={`col-span-2 text-right font-mono font-bold text-sm tabular-nums ${r.total_profit >= 0 ? "text-profit" : "text-loss"}`}>
                    {r.total_profit >= 0 ? "+" : ""}{fmt2(r.total_profit)}
                  </div>
                </motion.div>
              ))}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
